import { FunctionComponent, useEffect, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from './AppointmentCard.module.scss';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { dispatch, RootState } from 'store';
import { PetsState } from '@spike/pets-action';
import PetCard from 'components/UI/PetCard';
import moment, { Moment } from 'moment-timezone';
import AppointmentOptions from './AppointmentOptions';
import { DataVaraible, AppointmentMultiple, PetServiceWrapper, itemTypes, ItemData } from 'model/NewMultipleBooking';
import { Product } from '@spike/product-model';
import AddOnService from '@spike/addon-service-model';
import { AlertNewBooking } from '../UI/AlertNewBooking';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import Pet from '@spike/pet-model';
import { useMarketplace, useTimeZone } from 'hooks';
import { PetVaccine } from '@spike/medical-history-model';
import ServiceOrComplementaryDataInfo from './ServiceOrComplementaryDataInfo';
import DaycareDataInfo from './DaycareDataInfo';
import Service from 'model/Service';
import { EvaluationNotification } from 'components/Evaluation/EvaluationNotification';
import { EvaluationReport } from '@spike/report-model';
import { ServicesStatus } from 'reducers/services/ServicesState';
import { fetchEvaluationServiceThunk } from 'actions/services/ServicesActions';
import { isEvaluationEnabled } from 'utils/MarketingUtils';
import BoardingDataInfo from './BoardingDataInfo';
import TransportationDataInfo from './TransportationDataInfo';

interface Props {
    date: Moment;
    appointment: AppointmentMultiple;
    isEvaluation?: boolean;
    onAddService: () => void;
    onAddAddOn: (petId: number) => void;
    onAddProduct: (petId: number) => void;
    onAddCustomItem: (petId: number) => void;
    onDelete: (petId: number) => void;
    onAddEvaluation?: (petId: number, clientId: number) => void;
    onEdit: (
        petId: number,
        dateEdit: Moment,
        staffId?: number,
        itemData?: ItemData,
        service?: PetServiceWrapper,
        product?: Product,
        addOn?: AddOnService,
        custom?: string,
        type?: itemTypes,
        dateEnd?: Moment
    ) => void;
    onDeleteAppointment: (petId: number, value?: number | string, type?: itemTypes, dateToRemove?: Moment) => void;
    onShowActions: (
        petId: number,
        dateEdit: Moment,
        itemData?: ItemData,
        service?: PetServiceWrapper,
        staffId?: number,
        product?: Product,
        addOn?: AddOnService,
        custom?: string,
        type?: itemTypes,
        dateEnd?: Moment
    ) => void;
    onShowPetCard?: (petId: number) => void;
    onViewResults?: (evaluationReport?: EvaluationReport) => void;
}

export const AppointmentCard: FunctionComponent<Props> = props => {
    const timezone = useTimeZone();
    const marketplace = useMarketplace();
    const evaluationEnabled = isEvaluationEnabled(marketplace);

    const petsState = useSelector<RootState, PetsState>(state => state.pets);

    const serviceStatus = useSelector<RootState, ServicesStatus>(state => state.services.status);

    const evaluationService = useSelector<RootState, Service | null | undefined>(
        state => state.services.evaluationService
    );

    const [appointmentsSeparated, setAppointmentsSeparated] = useState<Array<Array<DataVaraible>> | undefined>(
        undefined
    );

    const [pet, setPet] = useState<Pet | undefined>(undefined);

    useEffect(() => {
        if (props.appointment.variableData && props.appointment.variableData.length > 0) {
            const appointments = props.appointment.variableData?.sort((wrapper, otherWrapper) =>
                wrapper.date.isBefore(otherWrapper.date) ? -1 : 1
            );
            const appointmentsSeparatedDate: Array<Array<DataVaraible>> | undefined = [];
            let arrayStart = 0;
            for (let i = 1; i <= appointments.length; i++) {
                if (i < appointments.length) {
                    if (appointments[i - 1].date.format('L') !== appointments[i].date.format('L')) {
                        appointmentsSeparatedDate.push(appointments.slice(arrayStart, i));
                        arrayStart = i;
                    }
                }
                if (i === appointments.length) {
                    appointmentsSeparatedDate.push(appointments.slice(arrayStart, appointments.length));
                }
            }
            setAppointmentsSeparated(appointmentsSeparatedDate);
        }
        if (props.appointment) {
            setPet(petsState.list.find((pet: Pet) => pet.id === props.appointment.petId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.appointment, petsState.list]);

    useEffect(() => {
        if (!evaluationService && serviceStatus !== ServicesStatus.Fetching && evaluationEnabled) {
            dispatch(fetchEvaluationServiceThunk());
        }
    }, [evaluationService, serviceStatus]);

    const haveEvaluationService = () => {
        if (!evaluationService || !appointmentsSeparated) {
            return false;
        }

        return appointmentsSeparated.some(appointmentGroup =>
            appointmentGroup.some(appointment => appointment.service?.service?.id === evaluationService.id)
        );
    };

    const countExpiredVaccines = (): number => {
        if (!pet?.medicalHistory?.vaccines) return 0;
        const today = moment().tz(timezone).clone().startOf('day');

        const expiredVaccines: Array<PetVaccine> = [];

        pet?.medicalHistory?.vaccines.forEach(vaccine => {
            const vaccineFounded = expiredVaccines.find(v => v.type?.id === vaccine.type?.id);
            if (vaccineFounded) {
                if (vaccineFounded?.expiration?.isBefore(vaccine.expiration)) {
                    const index = expiredVaccines.indexOf(vaccineFounded);
                    expiredVaccines[index] = { ...vaccine };
                }
            } else {
                if (vaccine.status === 'expired') {
                    expiredVaccines.push({ ...vaccine });
                }
            }
        });

        pet?.medicalHistory?.vaccines.forEach(vaccine => {
            const vaccineFounded = expiredVaccines.find(v => v.type?.id === vaccine.type?.id);
            if (vaccineFounded?.expiration?.isBefore(vaccine.expiration)) {
                const index = expiredVaccines.indexOf(vaccineFounded);
                expiredVaccines[index] = { ...vaccine };
            }
        });

        pet?.medicalHistory?.vaccines.forEach(vaccine => {
            const vaccineFounded = expiredVaccines.find(v => v.type?.id === vaccine.type?.id);
            if (vaccineFounded?.expiration?.isBefore(vaccine.expiration)) {
                const index = expiredVaccines.indexOf(vaccineFounded);
                expiredVaccines[index] = { ...vaccine };
            }
        });

        return expiredVaccines.filter(vaccine => {
            if (!vaccine.expiration) return false;
            return vaccine.expiration.isSameOrBefore(today, 'day');
        }).length;
    };

    const handleAddEvaluation = () => {
        if (props.onAddEvaluation && pet?.id && pet?.clientId) {
            props.onAddEvaluation(pet.id, pet.clientId);
        }
    };

    const handlerHideNotification = (): boolean => {
        let hide = true;
        if (!evaluationService?.evaluation?.category || !props.appointment.variableData) {
            return hide;
        }

        const categories = evaluationService.evaluation.category;

        for (let i = 0; i < categories.length; i++) {
            const category = categories[i];
            const item = props.appointment.variableData[i];

            switch (category) {
                case 'grooming':
                    if (props.appointment.variableData.some(item => item.service?.businessArea === 'grooming')) {
                        hide = false;
                    }
                    break;
                case 'daycare':
                    if (props.appointment.variableData.some(item => item.service?.businessArea === 'daycare')) {
                        hide = false;
                    }
                    break;
                case 'boarding':
                    if (props.appointment.variableData.some(item => item.service?.businessArea === 'boarding')) {
                        hide = false;
                    }
                    break;
            }
        }

        return hide;
    };

    return (
        <Box className={styles.container}>
            <EvaluationNotification
                onAddEvaluation={handleAddEvaluation}
                onViewResults={props.onViewResults}
                isEvaluation={haveEvaluationService()}
                hideNotification={handlerHideNotification()}
                evaluationReport={pet?.evaluationReport}
            />
            {countExpiredVaccines() > 0 && (
                <Box
                    className={styles.alertAndAddContainer}
                    onClick={() => props.onShowPetCard && props.onShowPetCard(props.appointment.petId)}
                >
                    <AlertNewBooking
                        className={styles.alert}
                        bWidth="2px"
                        icon={faExclamationTriangle}
                        iconSize="lg"
                        iconColor="#EF4F57"
                        bgColor="#FBB9BC33"
                        bdColor="#EF4F57"
                    >
                        <Typography className={styles.alertText}>
                            {pet?.name} has <span className={styles.bold}>{countExpiredVaccines()}</span> expired
                            vaccine. Please review & <a className={styles.update}>update</a>
                        </Typography>
                    </AlertNewBooking>
                </Box>
            )}
            <Box className={clsx(styles.petBox, styles.spacing)}>
                <Box className={styles.petCardContainer}>
                    <PetCard pet={petsState.list.find(pet => pet.id === props.appointment.petId)} />
                </Box>
                <Box className={styles.deleteBox}>
                    <Box
                        className={styles.deleteContainer}
                        onClick={() => props.onDelete(props.appointment.petId)}
                    >
                        <FontAwesomeIcon icon={faTrashCan} />
                    </Box>
                </Box>
            </Box>
            {appointmentsSeparated?.map((appts: Array<DataVaraible>) =>
                appts?.map((data: DataVaraible, index) =>
                    data.itemData?.flow === 'transportation' ? (
                        <TransportationDataInfo
                            key={index}
                            data={data}
                            index={index}
                            appts={appts}
                            appointment={props.appointment}
                            onEdit={props.onEdit}
                            onDeleteAppointment={props.onDeleteAppointment}
                            onShowActions={props.onShowActions}
                        />
                    ) : data.itemData?.flow === 'daycare' ? (
                        <DaycareDataInfo
                            key={`daycare-${data.service?.service?.id}-${data.date?.valueOf()}`}
                            hasFeedings={
                                (data.service?.feedings?.length && data.service?.feedings?.length > 0) || false
                            }
                            hasMedications={
                                (data.service?.medications?.length && data.service?.medications?.length > 0) || false
                            }
                            data={data}
                            index={index}
                            appts={appts}
                            appointment={props.appointment}
                            onEdit={props.onEdit}
                            onDeleteAppointment={props.onDeleteAppointment}
                            onShowActions={props.onShowActions}
                        />
                    ) : data.itemData?.flow === 'boarding' ? (
                        <BoardingDataInfo
                            key={index}
                            hasFeedings={
                                (data.service?.feedings?.length && data.service?.feedings?.length > 0) || false
                            }
                            hasMedications={
                                (data.service?.medications?.length && data.service?.medications?.length > 0) || false
                            }
                            data={data}
                            index={index}
                            appts={appts}
                            appointment={props.appointment}
                            onEdit={props.onEdit}
                            onDeleteAppointment={props.onDeleteAppointment}
                            onShowActions={props.onShowActions}
                        />
                    ) : (
                        <ServiceOrComplementaryDataInfo
                            key={index}
                            data={data}
                            index={index}
                            appts={appts}
                            appointment={props.appointment}
                            onEdit={props.onEdit}
                            onDeleteAppointment={props.onDeleteAppointment}
                            onShowActions={props.onShowActions}
                        />
                    )
                )
            )}
            {!props.isEvaluation && (
                <AppointmentOptions
                    className={styles.alertAndAddContainer}
                    onAddService={props.onAddService}
                    onAddAddOn={() => props.onAddAddOn(props.appointment.petId)}
                    onAddProduct={() => props.onAddProduct(props.appointment.petId)}
                    onAddCustomItem={() => props.onAddCustomItem(props.appointment.petId)}
                />
            )}
        </Box>
    );
};

export default AppointmentCard;
